/**
 * useEmergencyAccess Hook
 *
 * FERP CRITICAL: Emergency break-glass access management for life-critical workflows
 * Time-limited access tokens with complete audit trail
 *
 * Principles:
 * - All emergency access from backend (GraphQL mutations)
 * - Automatic security team notification
 * - Complete audit trail for all emergency actions
 * - Repository pattern (no direct API calls)
 * - Time-limited access tokens (1-24 hours)
 *
 * Regulatory Requirement: HIPAA §164.312(a)(2)(ii) emergency access procedures
 */

import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from './useCurrentTenant';
import { useAuth } from '../security/useAuth';

// ============================================================
// GraphQL Mutations
// ============================================================

/**
 * Request Emergency Access Mutation
 *
 * Backend: graphql_gateway/src/metadata_api/emergency_access_resolvers.rs
 * Creates time-limited access token with security notification
 */
export const REQUEST_EMERGENCY_ACCESS = gql`
  mutation RequestEmergencyAccess($input: EmergencyAccessInput!) {
    requestEmergencyAccess(input: $input) {
      accessToken
      incidentId
      expiresAt
      durationHours
      scenario
      justification
      userId
      userName
      tenantId
      auditTrailId
      securityNotificationSent
      createdAt
    }
  }
`;

/**
 * Revoke Emergency Access Mutation
 *
 * Backend: graphql_gateway/src/metadata_api/emergency_access_resolvers.rs
 * Revokes active emergency access token
 */
export const REVOKE_EMERGENCY_ACCESS = gql`
  mutation RevokeEmergencyAccess($incidentId: String!) {
    revokeEmergencyAccess(incidentId: $incidentId) {
      incidentId
      revokedAt
      revokedBy
      revokedByName
      success
    }
  }
`;

// ============================================================
// TypeScript Types
// ============================================================

export interface EmergencyAccessRequest {
  scenario: 'PatientSafetyEmergency' | 'SystemOutage' | 'RegulatoryAudit' | 'SecurityIncident' | 'DataBreachInvestigation';
  justification: string;
  durationHours: number;
}

export interface EmergencyAccessToken {
  accessToken: string;
  incidentId: string;
  expiresAt: string;
  durationHours: number;
  scenario: string;
  justification: string;
  userId: string;
  userName: string;
  tenantId: string;
  auditTrailId: string;
  securityNotificationSent: boolean;
  createdAt: string;
}

export interface RevokeEmergencyAccessResult {
  incidentId: string;
  revokedAt: string;
  revokedBy: string;
  revokedByName: string;
  success: boolean;
}

// ============================================================
// Hook Implementation
// ============================================================

/**
 * useEmergencyAccess Hook
 *
 * Manages emergency break-glass access
 *
 * Features:
 * - Request time-limited access tokens
 * - Automatic security team notification
 * - Complete audit trail
 * - Revoke active access
 * - Expiration countdown tracking
 */
export function useEmergencyAccess() {
  const { tenantId } = useCurrentTenant();
  const { user } = useAuth();

  const [currentAccessToken, setCurrentAccessToken] = useState<EmergencyAccessToken | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const [requestAccessMutation, { loading: requestingAccess }] = useMutation<Record<string, any>>(
    REQUEST_EMERGENCY_ACCESS,
    {
      onCompleted: (data: any) => {
        const token = data.requestEmergencyAccess;
        setCurrentAccessToken(token);

        notifications.show({
          title: 'Emergency Access Granted',
          message: `Access granted for ${token.durationHours} hours. Security team notified.`,
          color: 'orange',
          autoClose: 10000,
        });

        // Show warning
        notifications.show({
          title: 'Security Alert',
          message: 'All emergency actions will be audited and reviewed by security team.',
          color: 'red',
          autoClose: false,
        });
      },
      onError: (error: any) => {
        setError(error);

        notifications.show({
          title: 'Emergency Access Request Failed',
          message: error.message,
          color: 'red',
        });
      },
    }
  );

  const [revokeAccessMutation, { loading: revokingAccess }] = useMutation<Record<string, any>>(
    REVOKE_EMERGENCY_ACCESS,
    {
      onCompleted: (data: any) => {
        if (data.revokeEmergencyAccess.success) {
          setCurrentAccessToken(null);

          notifications.show({
            title: 'Emergency Access Revoked',
            message: 'Access has been revoked successfully',
            color: 'green',
          });
        }
      },
      onError: (error: any) => {
        notifications.show({
          title: 'Revocation Failed',
          message: error.message,
          color: 'red',
        });
      },
    }
  );

  /**
   * Request emergency break-glass access
   */
  const breakGlass = useCallback(
    async (scenario: string, justification: string, durationHours: number): Promise<EmergencyAccessToken> => {
      try {
        setError(null);

        if (!justification || justification.trim().length < 50) {
          throw new Error('Justification must be at least 50 characters');
        }

        if (!user) {
          throw new Error('User not authenticated');
        }

        const result = await requestAccessMutation({
          variables: {
            input: {
              tenantId,
              userId: user.id,
              userName: user.name,
              scenario,
              justification,
              durationHours,
            },
          },
        });

        return result.data?.requestEmergencyAccess ?? null;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to request emergency access');
        setError(err);
        throw err;
      }
    },
    [tenantId, user, requestAccessMutation]
  );

  /**
   * Revoke active emergency access
   */
  const revoke = useCallback(
    async (incidentId: string): Promise<RevokeEmergencyAccessResult> => {
      try {
        setError(null);

        const result = await revokeAccessMutation({
          variables: { incidentId },
        });

        return result.data?.revokeEmergencyAccess ?? null;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to revoke emergency access');
        setError(err);
        throw err;
      }
    },
    [revokeAccessMutation]
  );

  /**
   * Calculate time remaining until expiration
   */
  const getTimeRemaining = useCallback((accessToken: EmergencyAccessToken | null) => {
    if (!accessToken) {
      return {
        expired: true,
        hours: 0,
        minutes: 0,
        seconds: 0,
        label: 'No active access',
      };
    }

    const now = Date.now();
    const expiresAt = new Date(accessToken.expiresAt).getTime();
    const remaining = expiresAt - now;

    if (remaining <= 0) {
      return {
        expired: true,
        hours: 0,
        minutes: 0,
        seconds: 0,
        label: 'Expired',
      };
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    return {
      expired: false,
      hours,
      minutes,
      seconds,
      label: `${hours}h ${minutes}m ${seconds}s remaining`,
    };
  }, []);

  /**
   * Check if emergency access is active
   */
  const isAccessActive = useCallback(() => {
    if (!currentAccessToken) return false;
    const timeRemaining = getTimeRemaining(currentAccessToken);
    return !timeRemaining.expired;
  }, [currentAccessToken, getTimeRemaining]);

  /**
   * Clear current access token (after expiration or revocation)
   */
  const clearAccessToken = useCallback(() => {
    setCurrentAccessToken(null);
  }, []);

  return {
    // State
    currentAccessToken,
    requestingAccess,
    revokingAccess,
    error,

    // Actions
    breakGlass,
    revoke,
    clearAccessToken,

    // Helpers
    getTimeRemaining,
    isAccessActive,
  };
}
